
"use client";

import { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Container,
  FileText,
  Lock,
  RefreshCw,
  Server,
  TerminalSquare,
  XCircle,
} from "lucide-react";

type SnapshotState = "idle" | "running" | "done" | "error";

type TaskResult = {
  client?: string;
  result?: string;
  exit_code?: number | null;
  finished_at?: string | null;
  created_at?: string | null;
};

type LocalServicesPageProps = {
  embedded?: boolean;
  uuid?: string;
  nodeName?: string;
  nodeOs?: string;
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isWindows(os?: string) {
  return /windows/i.test(String(os || ""));
}

function buildLinuxCommand() {
  return `sh -lc 'echo "===== systemd 服务 =====";
if command -v systemctl >/dev/null 2>&1; then
  systemctl list-units --type=service --all --no-pager --no-legend 2>/dev/null | sed -n "1,240p";
else
  echo "systemctl not available";
fi;
echo "";
echo "===== Docker 容器 =====";
if command -v docker >/dev/null 2>&1; then
  docker ps -a --format "table {{.Names}}\\t{{.Image}}\\t{{.Status}}" 2>/dev/null || echo "docker command failed";
else
  echo "docker not installed";
fi;
echo "";
echo "===== 资源占用最高进程 =====";
ps -eo pid,ppid,comm,%cpu,%mem,rss,etime --sort=-%mem 2>/dev/null | sed -n "1,35p" || echo "ps unavailable";
echo "";
echo "===== 最近日志 =====";
if command -v journalctl >/dev/null 2>&1; then
  journalctl -n 40 --no-pager --output=short-iso 2>/dev/null | sed -n "1,80p";
else
  echo "journalctl not available";
fi'`;
}

function buildWindowsCommand() {
  return `powershell.exe -NoProfile -ExecutionPolicy Bypass -Command "$ErrorActionPreference='SilentlyContinue'; [Console]::OutputEncoding=[System.Text.Encoding]::UTF8; Write-Output '===== Windows 服务 ====='; Get-Service | Sort-Object Status,DisplayName | Select-Object Status,Name,DisplayName | Format-Table -AutoSize | Out-String -Width 260; Write-Output '===== Docker 容器 ====='; if (Get-Command docker -ErrorAction SilentlyContinue) { docker ps -a --format 'table {{.Names}}\t{{.Image}}\t{{.Status}}' } else { 'docker not installed' }; Write-Output '===== 资源占用最高进程 ====='; Get-Process | Sort-Object WS -Descending | Select-Object -First 40 Id,ProcessName,CPU,WS,StartTime | Format-Table -AutoSize | Out-String -Width 260; Write-Output '===== 最近系统事件 ====='; Get-WinEvent -LogName System -MaxEvents 30 | Select-Object TimeCreated,LevelDisplayName,ProviderName,Message | Format-Table -Wrap | Out-String -Width 260"`;
}

function buildCommand(os?: string) {
  return isWindows(os) ? buildWindowsCommand() : buildLinuxCommand();
}

function splitSections(raw: string) {
  const lines = String(raw || "").replace(/\r\n/g, "\n").split("\n");
  const sections: { title: string; body: string }[] = [];
  let current = "执行输出";
  let buf: string[] = [];
  const flush = () => {
    if (buf.length || !sections.length) sections.push({ title: current, body: buf.join("\n").trim() });
    buf = [];
  };
  for (const line of lines) {
    const m = line.match(/^=====\s*(.+?)\s*=====$/);
    if (m) {
      flush();
      current = m[1];
    } else {
      buf.push(line);
    }
  }
  flush();
  return sections.filter((s) => s.body || s.title !== "执行输出");
}

async function postJSON(url: string, body: any) {
  const resp = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => null);
  if (!resp.ok || json?.status === "error") {
    throw new Error(json?.message || json?.error || `HTTP ${resp.status}`);
  }
  return json?.data ?? json;
}

async function getJSON(url: string) {
  const resp = await fetch(url, { credentials: "include", cache: "no-store" });
  const json = await resp.json().catch(() => null);
  if (!resp.ok || json?.status === "error") {
    throw new Error(json?.message || json?.error || `HTTP ${resp.status}`);
  }
  return json?.data ?? json;
}

function StatusBadge({ state, text }: { state: "ok" | "warn" | "fail" | "muted"; text: string }) {
  const icon = state === "ok" ? <CheckCircle2 size={14} /> : state === "fail" ? <XCircle size={14} /> : state === "warn" ? <AlertTriangle size={14} /> : <Clock3 size={14} />;
  return <span className={`ds-local-real-badge is-${state}`}>{icon}{text}</span>;
}

function sectionIcon(title: string) {
  if (/docker|容器/i.test(title)) return <Container size={16} />;
  if (/journal|event|日志|事件/i.test(title)) return <FileText size={16} />;
  if (/process|进程/i.test(title)) return <Activity size={16} />;
  return <Server size={16} />;
}

export default function LocalServicesPage({ embedded = false, uuid, nodeName, nodeOs }: LocalServicesPageProps) {
  const [state, setState] = useState<SnapshotState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [result, setResult] = useState<TaskResult | null>(null);
  const command = useMemo(() => buildCommand(nodeOs), [nodeOs]);
  const sections = useMemo(() => splitSections(result?.result || ""), [result]);

  const refresh = async () => {
    if (!uuid) {
      setError("缺少节点 UUID，请从实例详情页进入。");
      setState("error");
      return;
    }
    setState("running");
    setError(null);
    setResult(null);
    setTaskId(null);
    try {
      const execResp = await postJSON("/api/admin/task/exec", { command, clients: [uuid] });
      const id = execResp?.task_id;
      if (!id) throw new Error("任务创建成功但没有返回 task_id");
      setTaskId(id);
      let last: TaskResult | null = null;
      for (let i = 0; i < 45; i += 1) {
        await sleep(i < 5 ? 1000 : 2000);
        try {
          const data = await getJSON(`/api/admin/task/${id}/result/${uuid}`);
          last = data as TaskResult;
          setResult(last);
          if (last && last.exit_code !== null && last.exit_code !== undefined) {
            setState(last.exit_code === 0 ? "done" : "error");
            if (last.exit_code !== 0) setError(`节点命令退出码：${last.exit_code}`);
            return;
          }
        } catch (err) {
          // Result may not exist immediately; keep polling.
          if (i > 5) setError(err instanceof Error ? err.message : String(err));
        }
      }
      setState("error");
      setError("等待节点返回结果超时");
      if (last) setResult(last);
    } catch (err) {
      setState("error");
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const badgeState = state === "done" ? "ok" : state === "error" ? "fail" : state === "running" ? "warn" : "muted";
  const badgeText = state === "done" ? "快照完成" : state === "error" ? "执行失败" : state === "running" ? "执行中" : "等待刷新";

  return (
    <div className={`ds-local-page is-real is-node-task ${embedded ? "is-embedded" : ""} w-full max-w-[1400px] mx-auto px-4 py-4 md:py-8`}>
      <div className="ds-local-hero">
        <div className="ds-local-hero-main">
          <div className="ds-local-eyebrow"><TerminalSquare size={15} /> 节点本地服务</div>
          <h1>本地服务</h1>
          <div className="ds-local-node-target">
            <span>当前节点</span>
            <strong>{nodeName || uuid || "未知节点"}</strong>
            <em>{nodeOs || "未知系统"}</em>
          </div>
          <div className="ds-local-hero-actions">
            <button className="ds-local-refresh" onClick={refresh} disabled={state === "running" || !uuid}>
              <RefreshCw size={15} className={state === "running" ? "ds-local-spin" : ""} />
              {state === "running" ? "读取中" : "刷新状态"}
            </button>
            <StatusBadge state={badgeState as any} text={badgeText} />
          </div>
        </div>
      </div>

      {!uuid ? (
        <div className="ds-local-locked-panel">
          <Lock size={22} />
          <div>
            <strong>请从实例详情页进入</strong>
            <p>这个页面需要当前节点 UUID，才能读取该服务器自己的本地服务。</p>
          </div>
        </div>
      ) : null}

      {error ? <div className="ds-local-auth is-locked"><div className="ds-local-auth-icon"><AlertTriangle size={18} /></div><div><div className="ds-local-auth-title">{error}</div><div className="ds-local-auth-desc">如果提示未授权，请先登录；如果提示 No clients connected，说明当前节点离线或 agent 没连上。</div></div></div> : null}

      {state === "idle" ? (
        <div className="ds-local-locked-panel">
          <TerminalSquare size={22} />
          <div>
            <strong>点击“刷新状态”获取当前节点快照</strong>
            <p>页面不会自动执行命令，也不会定时刷新；只有你手动点击时，才会对当前节点下发固定只读命令。</p>
          </div>
        </div>
      ) : null}

      {state === "running" ? (
        <div className="ds-local-loading"><RefreshCw className="ds-local-spin" size={20} />正在等待当前节点返回本地服务快照...{taskId ? ` 任务ID=${taskId}` : ""}</div>
      ) : null}

      {result ? (
        <>
          <div className="ds-local-real-kpis">
            <div className="ds-local-real-kpi"><span><Server size={16} />节点</span><strong>{nodeName || uuid}</strong><em>{uuid}</em></div>
            <div className="ds-local-real-kpi"><span><Activity size={16} />系统</span><strong>{nodeOs || "--"}</strong><em>{isWindows(nodeOs) ? "Windows 只读命令" : "Linux/Unix 只读命令"}</em></div>
            <div className="ds-local-real-kpi"><span><Clock3 size={16} />完成时间</span><strong>{result.finished_at || "--"}</strong><em>任务ID： {taskId || "--"}</em></div>
            <div className="ds-local-real-kpi"><span><TerminalSquare size={16} />退出码</span><strong>{result.exit_code ?? "等待"}</strong><em>固定只读命令</em></div>
          </div>

          {sections.map((section) => (
            <section className="ds-local-real-section" key={section.title}>
              <div className="ds-local-real-head">{sectionIcon(section.title)}<h2>{section.title}</h2></div>
              <pre className="ds-local-node-output">{section.body || "无输出"}</pre>
            </section>
          ))}
        </>
      ) : null}
    </div>
  );
}
